import { getDocs, doc, getDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase/config/firebase';
import { universitiesCol, tripsCol } from '../firebase/firestore/collections';
import { University } from '../types';
import { SEED_UNIVERSITIES } from '../constants/mockSeed';

const DELETED_UNIS_KEY = 'nashgo_deleted_university_ids';

export const getDeletedUniversityIds = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(DELETED_UNIS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const markUniversityDeleted = (id: string): void => {
  if (typeof window === 'undefined') return;
  try {
    const ids = getDeletedUniversityIds();
    if (!ids.includes(id)) {
      ids.push(id);
      localStorage.setItem(DELETED_UNIS_KEY, JSON.stringify(ids));
    }
  } catch (err) {
    console.warn('Could not store deleted university id:', err);
  }
};

// In-memory cache for ultra-fast instant UI rendering
let cachedAllUnis: { data: University[]; timestamp: number } | null = null;
let cachedActiveUnis: { data: University[]; timestamp: number } | null = null;

export const invalidateUniversityCache = () => {
  cachedAllUnis = null;
  cachedActiveUnis = null;
};

/**
 * Service managing University discovery, Firestore synchronization, and persistent deletion
 */
export const universityService = {
  getDeletedUniversityIds,
  markUniversityDeleted,
  invalidateCache: invalidateUniversityCache,

  /**
   * Delete a university permanently from Firestore database and state
   */
  async deleteUniversity(id: string): Promise<void> {
    invalidateUniversityCache();
    markUniversityDeleted(id);
    try {
      const docRef = doc(db, 'universities', id);
      await deleteDoc(docRef);
    } catch (err) {
      console.warn('Error deleting university document from Firestore:', err);
    }
  },

  /**
   * Fetch all universities registered in Firestore database, merged with SEED_UNIVERSITIES, excluding deleted ones
   */
  async getAllUniversities(forceRefresh = false): Promise<University[]> {
    if (!forceRefresh && cachedAllUnis && Date.now() - cachedAllUnis.timestamp < 60000) {
      return cachedAllUnis.data;
    }

    const deletedIds = getDeletedUniversityIds();
    try {
      const snapshot = await getDocs(universitiesCol);
      const map = new Map<string, University>();

      // Populate seed universities first
      SEED_UNIVERSITIES.forEach((u) => {
        if (!deletedIds.includes(u.id)) {
          map.set(u.id, u);
        }
      });

      // Overlay Firestore documents
      if (!snapshot.empty) {
        snapshot.docs.forEach((docSnap) => {
          if (!deletedIds.includes(docSnap.id)) {
            map.set(docSnap.id, { ...docSnap.data(), id: docSnap.id } as University);
          }
        });
      }

      const list = Array.from(map.values());
      cachedAllUnis = { data: list, timestamp: Date.now() };
      return list;
    } catch (err) {
      console.warn('Could not fetch universities from Firestore:', err);
      if (cachedAllUnis?.data && cachedAllUnis.data.length > 0) {
        return cachedAllUnis.data;
      }
      return SEED_UNIVERSITIES.filter((u) => !deletedIds.includes(u.id));
    }
  },

  /**
   * Fetch active universities registered in Firestore database, excluding deleted ones.
   * Also automatically includes any university that has open scheduled trips.
   */
  async getActiveUniversities(forceRefresh = false): Promise<University[]> {
    if (!forceRefresh && cachedActiveUnis && Date.now() - cachedActiveUnis.timestamp < 30000) {
      return cachedActiveUnis.data;
    }

    const deletedIds = getDeletedUniversityIds();
    try {
      const [snapshot, tripsSnap] = await Promise.all([
        getDocs(universitiesCol),
        getDocs(query(tripsCol, where('status', 'in', ['OPEN', 'FULL']))).catch(() => null),
      ]);

      const map = new Map<string, University>();

      // Include active seeds
      SEED_UNIVERSITIES.forEach((u) => {
        if (!deletedIds.includes(u.id) && u.active !== false && u.isActive !== false) {
          map.set(u.id, u);
        }
      });

      // Collect university IDs that have active scheduled trips
      const unisWithActiveTrips = new Set<string>();
      if (tripsSnap && !tripsSnap.empty) {
        tripsSnap.docs.forEach((d) => {
          const data = d.data();
          if (data?.universityId) {
            unisWithActiveTrips.add(data.universityId);
          }
        });
      }

      if (!snapshot.empty) {
        snapshot.docs.forEach((docSnap) => {
          if (deletedIds.includes(docSnap.id)) return;
          const u = { ...docSnap.data(), id: docSnap.id } as University;
          if ((u.active !== false && u.isActive !== false) || unisWithActiveTrips.has(u.id)) {
            map.set(u.id, u);
          }
        });
      }

      const list = Array.from(map.values());
      cachedActiveUnis = { data: list, timestamp: Date.now() };
      return list;
    } catch (err) {
      console.warn('Could not fetch active universities from Firestore:', err);
      if (cachedActiveUnis?.data && cachedActiveUnis.data.length > 0) {
        return cachedActiveUnis.data;
      }
      return SEED_UNIVERSITIES.filter((u) => !deletedIds.includes(u.id) && u.active !== false && u.isActive !== false);
    }
  },

  /**
   * Fetch single university by ID
   */
  async getUniversityById(id: string): Promise<University | null> {
    const deletedIds = getDeletedUniversityIds();
    if (deletedIds.includes(id)) return null;

    try {
      const docRef = doc(db, 'universities', id);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        return { ...snap.data(), id: snap.id } as University;
      }
    } catch (err) {
      console.warn('Error fetching university by id from Firestore:', err);
    }
    return null;
  },
};

